"use client"

import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { Download, FileSpreadsheet, Users, Image as ImageIcon, Trophy, Tag } from 'lucide-react'
import apiClient from '../lib/api-client'
import { API_BASE_URL } from '../config'

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF1919'];

export default function AnalyticsDashboard() {
  const [sponsors, setSponsors] = useState([])
  const [matches, setMatches] = useState([])
  const [images, setImages] = useState([])
  const [, setPlayers] = useState([])
  const [loading, setLoading] = useState(true)
  const [timeFilter, setTimeFilter] = useState('all') // all, 30days, 7days
  
  // Computed metrics
  const [metrics, setMetrics] = useState({
    totalImages: 0,
    processedImages: 0,
    failedImages: 0,
    totalSponsors: 0,
    totalMatches: 0,
    totalPlayers: 0,
    averageSponsorsPerImage: 0,
    averagePlayersPerImage: 0
  })

  const calculateMetrics = useCallback((imagesData, sponsorsData, matchesData, playersData) => {
    // Filter images by time if needed etst
    let filteredImages = imagesData
    if (timeFilter !== 'all') {
      const days = timeFilter === '7days' ? 7 : 30
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - days)
      
      filteredImages = imagesData.filter(img => {
        const imgDate = new Date(img.timestamp || img.createdAt || img.uploadedAt || 0)
        return imgDate >= cutoffDate
      })
    }

    const totalImages = filteredImages.length
    const processedImages = filteredImages.filter(img => 
      img.status === 'completed' || (img.sponsors && img.sponsors.length > 0) || (img.players && img.players.length > 0)
    ).length
    const failedImages = filteredImages.filter(img => img.status === 'failed').length

    // Calculate sponsor detections
    const totalSponsorDetections = filteredImages.reduce((sum, img) => 
      sum + (img.sponsors ? img.sponsors.length : 0), 0
    )
    const averageSponsorsPerImage = totalImages > 0 ? totalSponsorDetections / totalImages : 0

    // Calculate player detections
    const totalPlayerDetections = filteredImages.reduce((sum, img) => 
      sum + (img.players ? img.players.length : 0), 0
    )
    const averagePlayersPerImage = totalImages > 0 ? totalPlayerDetections / totalImages : 0

    setMetrics({
      totalImages,
      processedImages,
      failedImages,
      totalSponsors: sponsorsData.length,
      totalMatches: matchesData.length,
      totalPlayers: playersData.length,
      averageSponsorsPerImage: Math.round(averageSponsorsPerImage * 100) / 100,
      averagePlayersPerImage: Math.round(averagePlayersPerImage * 100) / 100
    })
  }, [timeFilter])

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      
      // Fetch all data in parallel
      const [sponsorsRes, matchesRes, imagesRes, playersRes] = await Promise.all([
        apiClient.get(`${API_BASE_URL}/sponsors`),
        apiClient.get(`${API_BASE_URL}/matches`),
        apiClient.get(`${API_BASE_URL}/images`),
        apiClient.get(`${API_BASE_URL}/players`)
      ])

      // Extract data arrays
      const sponsorsData = Array.isArray(sponsorsRes.data) ? sponsorsRes.data : 
                          sponsorsRes.data?.data || sponsorsRes.data?.sponsors || []
      const matchesData = Array.isArray(matchesRes.data) ? matchesRes.data : 
                         matchesRes.data?.data || matchesRes.data?.matches || []
      const imagesData = Array.isArray(imagesRes.data) ? imagesRes.data : 
                        imagesRes.data?.data || imagesRes.data?.images || []
      const playersData = Array.isArray(playersRes.data) ? playersRes.data : 
                         playersRes.data?.data || playersRes.data?.players || []

      setSponsors(sponsorsData)
      setMatches(matchesData)
      setImages(imagesData)
      setPlayers(playersData)

      // Calculate metrics
      calculateMetrics(imagesData, sponsorsData, matchesData, playersData)
      
    } catch (error) {
      console.error('Error loading analytics data:', error)
    } finally {
      setLoading(false)
    }
  }, [calculateMetrics])

  useEffect(() => {
    loadData()
  }, [loadData, timeFilter])

  // Get sponsor detection data for charts
  const getSponsorDetectionData = () => {
    let filteredImages = images
    if (timeFilter !== 'all') {
      const days = timeFilter === '7days' ? 7 : 30
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - days)
      
      filteredImages = images.filter(img => {
        const imgDate = new Date(img.timestamp || img.createdAt || img.uploadedAt || 0)
        return imgDate >= cutoffDate
      })
    }

    // Count sponsor detections
    const sponsorCounts = {}
    filteredImages.forEach(img => {
      if (img.sponsors && Array.isArray(img.sponsors)) {
        img.sponsors.forEach(sponsorId => {
          const sponsor = sponsors.find(s => s.id === sponsorId)
          const sponsorName = sponsor ? sponsor.name : `Unknown (${sponsorId})`
          sponsorCounts[sponsorName] = (sponsorCounts[sponsorName] || 0) + 1
        })
      }
    })

    return Object.entries(sponsorCounts)
      .map(([name, count]) => ({ name, detections: count }))
      .sort((a, b) => b.detections - a.detections)
      .slice(0, 10) // Top 10
  }

  // Get match distribution data
  const getMatchDistributionData = () => {
    let filteredImages = images
    if (timeFilter !== 'all') {
      const days = timeFilter === '7days' ? 7 : 30
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - days)
      
      filteredImages = images.filter(img => {
        const imgDate = new Date(img.timestamp || img.createdAt || img.uploadedAt || 0)
        return imgDate >= cutoffDate
      })
    }

    const matchCounts = {}
    filteredImages.forEach(img => {
      if (img.matchId) {
        const match = matches.find(m => m.id === img.matchId)
        const matchName = match ? match.title : `Unknown Match`
        matchCounts[matchName] = (matchCounts[matchName] || 0) + 1
      } else {
        matchCounts['No Match'] = (matchCounts['No Match'] || 0) + 1
      }
    })

    return Object.entries(matchCounts)
      .map(([name, count]) => ({ name, images: count }))
      .sort((a, b) => b.images - a.images)
  }

  // Export functions
  const exportToCSV = (data, filename) => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + Object.keys(data[0]).join(",") + "\n"
      + data.map(row => Object.values(row).join(",")).join("\n")
    
    const encodedUri = encodeURI(csvContent)
    const link = document.createElement("a")
    link.setAttribute("href", encodedUri)
    link.setAttribute("download", `${filename}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const exportSponsorReport = () => {
    const sponsorData = getSponsorDetectionData().map(item => ({
      'Sponsor Name': item.name,
      'Detection Count': item.detections,
      'Time Period': timeFilter === 'all' ? 'All Time' : timeFilter === '7days' ? 'Last 7 Days' : 'Last 30 Days'
    }))
    exportToCSV(sponsorData, `sponsor-detections-${timeFilter}-${new Date().toISOString().split('T')[0]}`)
  }

  const exportMatchReport = () => {
    const matchData = getMatchDistributionData().map(item => ({
      'Match Name': item.name,
      'Image Count': item.images,
      'Time Period': timeFilter === 'all' ? 'All Time' : timeFilter === '7days' ? 'Last 7 Days' : 'Last 30 Days'
    }))
    exportToCSV(matchData, `match-distribution-${timeFilter}-${new Date().toISOString().split('T')[0]}`)
  }

  const exportSummaryReport = () => {
    const summaryData = [{
      'Total Images': metrics.totalImages,
      'Processed Images': metrics.processedImages,
      'Failed Images': metrics.failedImages,
      'Processing Success Rate': metrics.totalImages > 0 ? `${Math.round((metrics.processedImages / metrics.totalImages) * 100)}%` : '0%',
      'Total Sponsors': metrics.totalSponsors,
      'Total Matches': metrics.totalMatches,
      'Total Players': metrics.totalPlayers,
      'Avg Sponsors per Image': metrics.averageSponsorsPerImage,
      'Avg Players per Image': metrics.averagePlayersPerImage,
      'Time Period': timeFilter === 'all' ? 'All Time' : timeFilter === '7days' ? 'Last 7 Days' : 'Last 30 Days',
      'Generated On': new Date().toISOString()
    }]
    exportToCSV(summaryData, `analytics-summary-${timeFilter}-${new Date().toISOString().split('T')[0]}`)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  const sponsorDetectionData = getSponsorDetectionData()
  const matchDistributionData = getMatchDistributionData()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
        <div className="flex items-center space-x-4">
          <Select value={timeFilter} onValueChange={setTimeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="30days">Last 30 Days</SelectItem>
              <SelectItem value="7days">Last 7 Days</SelectItem>
            </SelectContent>
          </Select>
          
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={exportSummaryReport}>
              <Download className="h-4 w-4 mr-1" />
              Summary
            </Button>
            <Button variant="outline" size="sm" onClick={exportSponsorReport}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />
              Sponsors
            </Button>
            <Button variant="outline" size="sm" onClick={exportMatchReport}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />
              Matches
            </Button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <ImageIcon className="h-4 w-4 mr-2" />
              Total Images
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" title="Total number of images uploaded to the system">{metrics.totalImages}</div>
            <p className="text-xs text-muted-foreground" title="Images that have been analyzed by AI vs failed processing">
              {metrics.processedImages} processed, {metrics.failedImages} failed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Tag className="h-4 w-4 mr-2" />
              Sponsors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" title="Total number of unique sponsors in the system">{metrics.totalSponsors}</div>
            <p className="text-xs text-muted-foreground" title="Average number of sponsor detections per image">
              {metrics.averageSponsorsPerImage} avg per image
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Trophy className="h-4 w-4 mr-2" />
              Matches
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalMatches}</div>
            <p className="text-xs text-muted-foreground">
              {Math.round((metrics.totalImages / Math.max(metrics.totalMatches, 1)) * 100) / 100} avg images per match
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Users className="h-4 w-4 mr-2" />
              Players
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalPlayers}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.averagePlayersPerImage} avg per image
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sponsor Detections */}
        <Card>
          <CardHeader>
            <CardTitle>Top Sponsor Detections</CardTitle>
            <CardDescription>
              Most frequently detected sponsors in images
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sponsorDetectionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={80}
                    fontSize={12}
                  />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="detections" fill="#0088FE" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Match Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Images per Match</CardTitle>
            <CardDescription>
              Distribution of images across matches
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={matchDistributionData.slice(0, 8)} // Top 8 to keep readable
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="images"
                  >
                    {matchDistributionData.slice(0, 8).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Processing Status */}
      <Card>
        <CardHeader>
          <CardTitle>Processing Status</CardTitle>
          <CardDescription>
            Success rate and failure analysis
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Success Rate</span>
              <span className="text-lg font-bold text-green-600">
                {metrics.totalImages > 0 ? Math.round((metrics.processedImages / metrics.totalImages) * 100) : 0}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full" 
                style={{ 
                  width: `${metrics.totalImages > 0 ? (metrics.processedImages / metrics.totalImages) * 100 : 0}%` 
                }}
              ></div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="font-medium text-green-600">{metrics.processedImages}</div>
                <div className="text-muted-foreground">Processed</div>
              </div>
              <div className="text-center">
                <div className="font-medium text-red-600">{metrics.failedImages}</div>
                <div className="text-muted-foreground">Failed</div>
              </div>
              <div className="text-center">
                <div className="font-medium text-gray-600">{metrics.totalImages - metrics.processedImages - metrics.failedImages}</div>
                <div className="text-muted-foreground">Pending</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}